import { capitalize, remove } from "lodash";

import {
  generateDescendingRange,
  generateDescendingPairsRange
} from "views/documents/state/libs/generate-descending";
import { ArchiveNodeOption } from "models/ArchiveNodeOption";
import { ArchiveItem } from "models/ArchiveItem";
import { ExternalUser, ExternalUserRole } from "models/customers/customers";

type FolderYearPeriod = {
  periodName: string;
  sortOrder: number;
};

type CustomerFolders = ArchiveItem[] | CustomerSelectedDocumentOption[];

export const NODE_TYPE_FOLDER = "folder";
export const NODE_TYPE_ROOT = "root";

// Declaration and year end folder IDs
export const FOLDER_ID_BOK_DEKL = "bokdekl";
const FOLDER_ID_DIGITAL_YEAR_ACT_SUFFIX = "digars";
const FOLDER_ID_INCOME_DECLARATION_SUFFIX = "inkdekl";
const FOLDER_ID_OTHER_SUFFIX = "ovr";

export const YEAR_END_FOLDER_ID_SUFFIXES = [
  FOLDER_ID_DIGITAL_YEAR_ACT_SUFFIX,
  FOLDER_ID_INCOME_DECLARATION_SUFFIX,
  FOLDER_ID_OTHER_SUFFIX
];

/**
 * Extracts report folder IDs from customer documents.
 *
 * @param {Array<{id: string}>} customerFolders - Array of customer folder objects, each with at least an `id` property.
 * @returns {string[]} Array of folder IDs that start with "reports".
 *
 * @example
 * // Input:
 * // [
 * //   { id: "reports-2022"},
 * //   { id: "other-2022"},
 * //   { id: "reports-2021"}
 * // ]
 * // Output:
 * // ["reports-2022", "reports-2021"]
 */
const getReportsFolders = (customerFolders: CustomerFolders) => {
  if (!Array.isArray(customerFolders)) {
    return [];
  }

  return (customerFolders as Array<{ id: string }>)
    .filter(
      (folder) =>
        typeof folder.id === "string" && isReportsFolder(folder.id)
    )
    .map((item) => item.id);
};

type Folder = {
  id: string;
  name: string;
  hidden: boolean;
  nodes: Folder[];
  locked?: boolean;
  enabled?: boolean;
};

/**
 * Builds a hierarchical folder tree structure from a list of report IDs.
 *
 * Each input ID should be a string in the format "reports-category-subcategory-...".
 * The function parses these IDs and constructs a nested tree of folders,
 * where each part of the ID after "reports" becomes a folder node.
 *
 * The returned tree has a root node with id "reports", and nested nodes for each category/subcategory.
 * Returns null if no report IDs are provided.
 *
 * Example:
 * Input:
 *   [
 *     "reports-finance-2023",
 *   ]
 * Output:
 *   {
 *     id: "reports",
 *     name: "Reports",
 *     hidden: false,
 *     locked: true,
 *     enabled: true,
 *     nodes: [
 *       {
 *         id: "reports-finance",
 *         name: "finance",
 *         hidden: false,
 *         locked: true,
 *         enabled: true,
 *         nodes: [
 *           {
 *             id: "reports-finance-2023",
 *             name: "2023",
 *             hidden: false,
 *             locked: true,
 *             enabled: true,
 *             nodes: []
 *           },
 *         ]
 *       },

 *     ]
 *   }
 *
 * @param {string[]} ids - Array of report IDs in the format "reports-category-subcategory-...".
 * @returns {Folder | null} Hierarchical folder tree structure, or null if no IDs provided.
 */
function buildReportsFolderTree(ids: string[]): Folder | null {
  // Return null if no report IDs are provided to avoid creating empty Reports folder
  if (!ids || ids.length === 0) {
    return null;
  }

  const root: Folder = {
    id: "reports",
    name: "Rapporter",
    hidden: false,
    locked: true,
    enabled: true,
    nodes: []
  };

  for (const id of ids) {
    const trimmed = id.replace(/^reports-/, "");

    const parts = trimmed.split("-");

    let current = root;
    let accumulatedId = "reports";

    for (const part of parts) {
      accumulatedId += "-" + part;
      const searchId = accumulatedId;
      let node = current.nodes.find((n) => n.id === searchId);
      const folderName = part === part.toUpperCase() ? part : capitalize(part);
      if (!node) {
        node = {
          id: accumulatedId,
          name: folderName,
          hidden: false,
          locked: true,
          enabled: true,
          nodes: []
        };
        current.nodes.push(node);
      }
      current = node;
    }
  }

  return root;
}

export const legacySuffixes = [
  "balres",
  "bokspec",
  "huvbok",
  "nyckel",
  "sysdok",
  "verifik",
  "arskontr",
  "arsbok"
];

export const getLegacyNodes = (financialFolderPrefix: string) => [
  {
    id: `${financialFolderPrefix}-balres`,
    name: "Balans- och resultatrapport",
    hidden: true,
    nodes: [],
    sortOrder: 1000
  },
  {
    id: `${financialFolderPrefix}-bokspec`,
    name: "Bokslutsspecifikationer",
    hidden: true,
    nodes: [],
    sortOrder: 1000
  },
  {
    id: `${financialFolderPrefix}-huvbok`,
    name: "Huvudbok",
    hidden: true,
    nodes: [],
    sortOrder: 1000
  },
  {
    id: `${financialFolderPrefix}-nyckel`,
    name: "Lönsamhetsutveckling/Nyckeltal",
    hidden: true,
    nodes: [],
    sortOrder: 1000
  },
  {
    id: `${financialFolderPrefix}-sysdok`,
    name: "Systemdokumentation",
    hidden: true,
    nodes: [],
    sortOrder: 1000
  },
  {
    id: `${financialFolderPrefix}-verifik`,
    name: "Verifikationslista",
    hidden: true,
    nodes: [],
    sortOrder: 1000
  },
  {
    id: `${financialFolderPrefix}-arskontr`,
    name: "Årsbesked, kontrolluppgifter",
    hidden: true,
    nodes: [],
    sortOrder: 1000
  },
  {
    id: `${financialFolderPrefix}-arsbok`,
    name: "Årsbokslut/Årsredovisning",
    hidden: true,
    nodes: [],
    sortOrder: 1000
  }
];

export function filterYearEndFolders(
  item: ArchiveItem,
  customerDocuments: CustomerFolders
) {
  const yearEndAndDeclarationFolderRegexp =
    /eksk-fin-(\d{4}|\d{4}\/\d{4})-bokdekl-/gm;

  const isYearEndFolder = yearEndAndDeclarationFolderRegexp.test(item.id);
  if (!isYearEndFolder) {
    return true;
  }
  const isEnabledYearEndFolder = YEAR_END_FOLDER_ID_SUFFIXES.some((id) =>
    item.id.includes(id)
  );
  if (isEnabledYearEndFolder) {
    return true;
  }
  const isHiddenFolder = isYearEndArchiveItemHidden(item, customerDocuments);
  return !item.empty || !isHiddenFolder;
}

function isYearEndArchiveItemHidden(
  item: ArchiveItem,
  customerDocuments: CustomerFolders
): boolean {
  const ekskFolder = getDocumentArchiveOptions(customerDocuments).nodes.filter(
    (f) => {
      if (f.id === "eksk") {
        return f;
      }
      return false;
    }
  )[0];
  const ekskFolderOld = ekskFolder.nodes.filter((f) => {
    if (f.id === "eksk-earlier") {
      return f;
    }
    return false;
  })[0];
  let isHidden = true;
  ekskFolder.nodes.filter((fp) => {
    if (fp.id === item.parentId) {
      fp.nodes.filter((f) => {
        if (f.id === item.id) {
          isHidden = f.hidden;
        }
        return 0;
      });
    }
    return 0;
  });
  if (isHidden) {
    ekskFolderOld.nodes.filter((fp) => {
      if (fp.id === item.parentId) {
        fp.nodes.filter((f) => {
          if (f.id === item.id) {
            isHidden = f.hidden;
          }
          return 0;
        });
      }
      return 0;
    });
  }
  return isHidden;
}

function getFinancialFoldersForPeriod(
  parentId: string,
  useLegacyNodes: boolean
): ArchiveNodeOption[] {
  const financialFolderPrefix = parentId + "-" + FOLDER_ID_BOK_DEKL;
  let legacyNodes = getLegacyNodes(financialFolderPrefix);
  if (useLegacyNodes) {
    legacyNodes = legacyNodes.map((node) => {
      node.hidden = false;
      return node;
    });
  }

  const financialNodes: ArchiveNodeOption[] = [
    {
      id: `${financialFolderPrefix}-${FOLDER_ID_DIGITAL_YEAR_ACT_SUFFIX}`,
      name: "Digital årsakt",
      hidden: false,
      nodes: [],
      sortOrder: 1000
    },
    {
      id: `${financialFolderPrefix}-${FOLDER_ID_INCOME_DECLARATION_SUFFIX}`,
      name: "Inkomstdeklaration",
      hidden: false,
      nodes: [],
      sortOrder: 1000
    },
    {
      id: `${financialFolderPrefix}-${FOLDER_ID_OTHER_SUFFIX}`,
      name: "Övrigt",
      hidden: false,
      nodes: [],
      sortOrder: 1000
    }
  ];

  return [...legacyNodes, ...financialNodes];
}

function getAccountingFolders(parentId: string): ArchiveNodeOption[] {
  const accountingId = parentId + "-lopr";

  return [
    {
      id: accountingId + "-budgprog",
      name: "Budget, kalkyl och prognoser",
      hidden: false,
      nodes: []
    },
    {
      id: accountingId + "-kontut",
      name: "Kontoutdrag",
      hidden: false,
      nodes: []
    },
    {
      id: accountingId + "-mrap",
      name: "Månadsrapporter",
      hidden: false,
      nodes: []
    },
    {
      id: accountingId + "-skattavg",
      name: "Skatter och avgifter",
      hidden: false,
      nodes: []
    },
    {
      id: accountingId + "-ovr",
      name: "Övrigt",
      hidden: false,
      nodes: []
    }
  ];
}

function getYearEndFoldersForPeriods(
  parentId: string,
  useLegacyNodes: boolean,
  periods: FolderYearPeriod[]
): ArchiveNodeOption[] {
  const res = periods.map((period) => {
    const id = `${parentId}-${period.periodName}`;
    const nodeName = `Bokslut och deklaration ${period.periodName}`;
    return {
      id,
      name: nodeName,
      nodes: getFinancialFoldersForPeriod(id, useLegacyNodes),
      hidden: false,
      sortOrder: 100 + period.sortOrder
    };
  });
  return res;
}

function getAccountingFoldersForPeriods(
  parentId: string,
  periods: FolderYearPeriod[]
): ArchiveNodeOption[] {
  return periods.map((period) => {
    const id = `${parentId}-${period.periodName}`;
    const nodeName = `Redovisning ${period.periodName}`;

    return {
      id,
      name: nodeName,
      nodes: getAccountingFolders(id),
      hidden: false,
      sortOrder: 200 + period.sortOrder
    };
  });
}

function getPaymentFoldersForPeriods(
  parentId: string,
  periods: FolderYearPeriod[]
): ArchiveNodeOption[] {
  return periods.map((period) => {
    const id = `${parentId}-${period.periodName}`;
    const nodeName = `Lön ${period.periodName}`;

    return {
      id,
      name: nodeName,
      hidden: false,
      sortOrder: 300 + period.sortOrder,
      nodes: [
        generateNode(id, "Januari", 0),
        generateNode(id, "Februari", 1),
        generateNode(id, "Mars", 2),
        generateNode(id, "April", 3),
        generateNode(id, "Maj", 4),
        generateNode(id, "Juni", 5),
        generateNode(id, "Juli", 6),
        generateNode(id, "Augusti", 7),
        generateNode(id, "September", 8),
        generateNode(id, "Oktober", 9),
        generateNode(id, "November", 10),
        generateNode(id, "December", 11),
        {
          id: `${id}-ovrigt`,
          hidden: false,
          name: "Övrigt - Arbetsgivarintyg, Lönebidrag, KFM, FK",
          nodes: []
        },
        {
          id: `${id}-arsrapportering`,
          hidden: false,
          name: "Årsrapportering",
          nodes: []
        }
      ]
    };
  });
}

function generateNode(
  parentId: string,
  period: string,
  sortOrder = 0
): ArchiveNodeOption {
  const id = parentId + "-" + period;

  return {
    id,
    enabled: false,
    hidden: false,
    name: period,
    nodes: [],
    sortOrder
  };
}

/**
 * This function returns a full configuration of folder tree.
 *
 * Used to set/change the folder structure of a customer in the Customer Service Wizard.
 * Also used for some rendering logic in the Document Archive.
 * NOTE: In the counter in UserServiceExpandableListItem.tsx
 * we have a logic that filters out the hidden legacy folders
 * for the counter only visible legacy folders are taken into account
 * any changes made here regarding visibility of legacy folders must be reflected in
 * UserServiceExpandableListItem.tsx in function getUsedLegacyNodes
 * implemented by: https://lrfkonsult.atlassian.net/browse/ML-2138
 *
 * @param {CustomerFolders} customerDocuments - Used for fetching customer documents and dynamically getting reports folders".
 * @returns {ArchiveNodeOption} Hierarchical folder tree structure.
 */
export function getDocumentArchiveOptions(
  customerDocuments: CustomerFolders
): ArchiveNodeOption {
  return {
    id: "root",
    name: "Dokumentarkiv",
    enabled: true,
    hidden: false,
    nodes: [
      {
        id: "fgha",
        name: "Företagshandlingar",
        locked: true,
        enabled: true,
        hidden: false,
        sortOrder: 2,
        nodes: [
          {
            id: "fgha-arshjul",
            name: "Årshjul",
            locked: true,
            enabled: true,
            hidden: false,
            nodes: []
          },
          {
            id: "fgha-bolag",
            name: "Bolags- och företagshandlingar",
            locked: true,
            enabled: true,
            hidden: false,
            nodes: []
          },
          {
            id: "fgha-uppg",
            name: "Fastighetsuppgifter",
            locked: true,
            enabled: true,
            hidden: false,
            nodes: []
          },
          {
            id: "fgha-forbrev",
            name: "Försäkringsbrev",
            locked: true,
            enabled: true,
            hidden: false,
            nodes: []
          },
          {
            id: "fgha-pers",
            name: "Personal",
            locked: true,
            enabled: true,
            hidden: false,
            nodes: []
          },
          {
            id: "fgha-uppavt",
            name: "Uppdragsavtal",
            locked: true,
            enabled: true,
            hidden: false,
            nodes: []
          },
          {
            id: "fgha-ovr",
            name: "Övriga handlingar och avtal",
            locked: true,
            enabled: true,
            hidden: false,
            nodes: []
          }
        ]
      },
      // Only include reports folder if there are actual report folders
      ...(() => {
        const reportsFolder = buildReportsFolderTree(
          getReportsFolders(customerDocuments)
        );
        return reportsFolder ? [reportsFolder] : [];
      })(),
      {
        id: "eksk",
        header: true,
        hidden: false,
        name: "Ekonomi och skatt",
        nodes: [
          ...getAccountingFoldersForPeriods("eksk-red", [
            ...generateDescendingRange(2027, 8).map((r) => ({
              periodName: r.toString(),
              sortOrder: 2024 - r
            })),
            ...generateDescendingPairsRange(2027, 8).map((pair) => {
              return {
                periodName: `${pair[0]}/${pair[1]}`,
                sortOrder: 2024 - (pair[0] + pair[1]) / 2
              };
            })
          ]),
          ...getYearEndFoldersForPeriods("eksk-fin", false, [
            ...generateDescendingRange(2027, 6).map((r) => ({
              periodName: r.toString(),
              sortOrder: 2024 - r
            })),
            ...generateDescendingPairsRange(2027, 5).map((pair) => {
              return {
                periodName: `${pair[0]}/${pair[1]}`,
                sortOrder: 2024 - (pair[0] + pair[1]) / 2
              };
            })
          ]),
          ...getYearEndFoldersForPeriods("eksk-fin", true, [
            ...generateDescendingRange(2020, 3).map((r) => ({
              periodName: r.toString(),
              sortOrder: 2025 - r
            })),
            ...generateDescendingPairsRange(2022, 4).map((pair) => {
              return {
                periodName: `${pair[0]}/${pair[1]}`,
                sortOrder: 2025 - (pair[0] + pair[1]) / 2
              };
            })
          ]),
          ...getPaymentFoldersForPeriods("eksk-lon", [
            ...generateDescendingRange(2027, 8).map((r) => ({
              periodName: r.toString(),
              sortOrder: 2024 - r
            }))
          ]),
          {
            id: "eksk-earlier",
            name: "Tidigare år",
            hidden: false,
            nodes: [
              ...getAccountingFoldersForPeriods("eksk-red", [
                ...generateDescendingRange(2018, 14).map((r) => ({
                  periodName: r.toString(),
                  sortOrder: 2018 - r
                })),
                ...generateDescendingPairsRange(2018, 13).map((pair) => {
                  return {
                    periodName: `${pair[0]}/${pair[1]}`,
                    sortOrder: 2018 + 1 - (pair[0] + pair[1]) / 2
                  };
                })
              ]),
              ...getYearEndFoldersForPeriods("eksk-fin", true, [
                ...generateDescendingRange(2018, 14).map((r) => ({
                  periodName: r.toString(),
                  sortOrder: 2018 - r
                })),
                ...generateDescendingPairsRange(2018, 13).map((pair) => {
                  return {
                    periodName: `${pair[0]}/${pair[1]}`,
                    sortOrder: 2018 + 1 - (pair[0] + pair[1]) / 2
                  };
                })
              ]),
              ...getPaymentFoldersForPeriods("eksk-lon", [
                ...generateDescendingRange(2018, 14).map((r) => ({
                  periodName: r.toString(),
                  sortOrder: 2018 - r
                }))
              ])
            ]
          }
        ]
      },
      {
        id: "jur",
        name: "Juridik",
        hidden: false,
        nodes: [
          {
            id: "jur-fast",
            name: "Fastighetsrätt",
            hidden: false,
            nodes: [
              {
                id: "jur-fast-lant",
                name: "Lantmäterihandlingar",
                nodes: [],
                hidden: false
              },
              {
                id: "jur-fast-markers",
                name: "Markintrång",
                nodes: [],
                hidden: false
              },
              {
                id: "jur_fast-arr",
                name: "Arrende/nyttjanderätter",
                nodes: [],
                hidden: false
              }
            ]
          },
          {
            id: "jur-famr",
            name: "Familjerätt",
            hidden: false,
            nodes: [
              {
                id: "jur-famr-aktavt",
                name: "Äktenskapsförord/samboavtal",
                hidden: false,
                nodes: []
              },
              {
                id: "jur-famr-test",
                name: "Testamente",
                hidden: false,
                nodes: []
              },
              {
                id: "jur-famr-boarv",
                name: "Bouppteckning och arvsskifte",
                hidden: false,
                nodes: []
              },
              {
                id: "jur-famr-gava",
                name: "Gåvor",
                hidden: false,
                nodes: []
              }
            ]
          },
          {
            id: "jur-affr",
            name: "Affärsjuridik",
            hidden: false,
            nodes: [
              {
                id: "jur-affr-kop",
                name: "Köp och avtal",
                hidden: false,
                nodes: []
              },
              {
                id: "jur-affr-tvist",
                name: "Tvister",
                hidden: false,
                nodes: []
              },
              {
                id: "jur-affr-kompavt",
                name: "Kompanjonavtal",
                hidden: false,
                nodes: []
              },
              {
                id: "jur-affr-arb",
                name: "Arbetsrätt",
                hidden: false,
                nodes: []
              }
            ]
          },
          {
            id: "jur-gen",
            name: "Generationsskifte",
            hidden: false,
            nodes: [
              {
                id: "jur-gen-avtjur",
                name: "Avtal och juridik",
                hidden: false,
                nodes: []
              },
              {
                id: "jur-gen-fast",
                name: "Fastighet",
                hidden: false,
                nodes: []
              },
              {
                id: "jur-gen-dekskatt",
                name: "Deklaration och skatt",
                hidden: false,
                nodes: []
              },
              {
                id: "jur-gen-plankalk",
                name: "Affärsplanering och ekonomikalkyler",
                hidden: false,
                nodes: []
              },
              {
                id: "jur-gen-pmsamkorr",
                name: "PM/Sammanställningar/korrespondens",
                hidden: false,
                nodes: []
              }
            ]
          }
        ]
      },
      {
        id: "affr",
        name: "Affärsrådgivning",
        hidden: false,
        sortOrder: 1,
        nodes: [
          {
            id: "affr-eu",
            name: "EU-stöd",
            hidden: false,
            nodes: [
              {
                id: "affr-eu-sam-2025",
                name: "SAM-ansökan 2025",
                hidden: false,
                nodes: []
              },
              {
                id: "affr-eu-sam-2024",
                name: "SAM-ansökan 2024",
                hidden: false,
                nodes: []
              },
              {
                id: "affr-eu-sam-2023",
                name: "SAM-ansökan 2023",
                hidden: false,
                nodes: []
              },
              {
                id: "affr-eu-sam-2022",
                name: "SAM-ansökan 2022",
                hidden: false,
                nodes: []
              },
              {
                id: "affr-eu-sam-2021",
                name: "SAM-ansökan 2021",
                hidden: false,
                nodes: []
              },
              {
                id: "affr-eu-sam-2020",
                name: "SAM-ansökan 2020",
                hidden: false,
                nodes: []
              },
              {
                id: "affr-eu-sam-2019",
                name: "SAM-ansökan 2019",
                hidden: false,
                nodes: []
              },
              {
                id: "affr-eu-sam-2018",
                name: "SAM-ansökan 2018",
                hidden: false,
                nodes: []
              },
              {
                id: "affr-eu-sam-2017",
                name: "SAM-ansökan 2017",
                hidden: false,
                nodes: []
              },
              {
                id: "affr-eu-sam-2016",
                name: "SAM-ansökan 2016",
                hidden: false,
                nodes: []
              },
              {
                id: "affr-eu-sam",
                name: "SAM-ansökan",
                hidden: false,
                nodes: []
              },
              {
                id: "affr-eu-invest-2025",
                name: "Investeringsstöd 2025",
                hidden: false,
                nodes: []
              },
              {
                id: "affr-eu-invest-2024",
                name: "Investeringsstöd 2024",
                hidden: false,
                nodes: []
              },
              {
                id: "affr-eu-invest-2023",
                name: "Investeringsstöd 2023",
                hidden: false,
                nodes: []
              },
              {
                id: "affr-eu-invest-2022",
                name: "Investeringsstöd 2022",
                hidden: false,
                nodes: []
              },
              {
                id: "affr-eu-invest-2021",
                name: "Investeringsstöd 2021",
                hidden: false,
                nodes: []
              },
              {
                id: "affr-eu-invest-2020",
                name: "Investeringsstöd 2020",
                hidden: false,
                nodes: []
              },
              {
                id: "affr-eu-invest-2019",
                name: "Investeringsstöd 2019",
                hidden: false,
                nodes: []
              },
              {
                id: "affr-eu-invest-2018",
                name: "Investeringsstöd 2018",
                hidden: false,
                nodes: []
              },
              {
                id: "affr-eu-invest-2017",
                name: "Investeringsstöd 2017",
                hidden: false,
                nodes: []
              },
              {
                id: "affr-eu-invest-2016",
                name: "Investeringsstöd 2016",
                hidden: false,
                nodes: []
              },
              {
                id: "affr-eu-invest",
                name: "Investeringsstöd",
                hidden: false,
                nodes: []
              },
              {
                id: "affr-eu-ovr",
                name: "Övrigt-EU",
                hidden: false,
                nodes: []
              }
            ]
          },
          {
            id: "affr-ledn",
            name: "Företagsledning",
            hidden: false,
            nodes: [
              {
                id: "affr-ledn-malf",
                name: "Målformulering",
                hidden: false,
                nodes: []
              },
              {
                id: "affr-ledn-affplan",
                name: "Affärsplan",
                hidden: false,
                nodes: []
              },
              {
                id: "affr-ledn-org",
                name: "Organisation",
                hidden: false,
                nodes: []
              },
              {
                id: "affr-ledn-frad",
                name: "Företagsråd",
                hidden: false,
                nodes: []
              }
            ]
          },
          {
            id: "affr-miljo",
            name: "Miljö",
            hidden: false,
            nodes: [
              {
                id: "affr-miljo-konsbeskr",
                name: "Miljökonsekvensbeskrivning",
                hidden: false,
                nodes: []
              },
              {
                id: "affr-miljo-tillst",
                name: "Miljötillstånd m.m.",
                hidden: false,
                nodes: []
              },
              {
                id: "affr-miljo-energi",
                name: "Energi",
                hidden: false,
                nodes: []
              }
            ]
          },
          {
            id: "affr-ovr",
            name: "Affärsrådgivning övrigt",
            hidden: false,
            nodes: []
          }
        ]
      },
      {
        id: "fstgh",
        name: "Fastighet",
        hidden: false,
        nodes: [
          {
            id: "fstgh-affr",
            name: "Fastighetsaffär",
            hidden: false,
            nodes: []
          },
          {
            id: "fstgh-vardr",
            name: "Värdering",
            hidden: false,
            nodes: []
          },
          {
            id: "fstgh-skogp",
            name: "Skogsbruksplan",
            hidden: false,
            nodes: []
          },
          {
            id: "fstgh-ovr",
            name: "Övrigt",
            hidden: false,
            nodes: []
          }
        ]
      },
      {
        id: "history",
        name: "Historik",
        hidden: false,
        nodes: []
      }
    ]
  };
}

type CustomerSelectedDocumentOption = {
  customerChoice: string | boolean;
  id: string;
  userIds: string[];
};

// Add any mandatory folders, set permissions and clean up unused document services
export function setupCustomerSelectedFolders(
  customerSelectedDocumentOptions: CustomerSelectedDocumentOption[]
) {
  // Add any mandatory folders to the customer selected folders
  addLockedDocumentServicesToCustomerSelectedFolders(
    getDocumentArchiveOptions(customerSelectedDocumentOptions),
    customerSelectedDocumentOptions
  );

  const customerSelectedDocumentOptionsClean = cleanUnusedDocumentServices(
    getDocumentArchiveOptions(customerSelectedDocumentOptions),
    customerSelectedDocumentOptions
  );

  return customerSelectedDocumentOptionsClean;
}

// Check if the node with the given search ID exists in the node tree
function existsInTree(
  node: ArchiveNodeOption,
  searchId: string
): ArchiveNodeOption | undefined {
  if (node.id === searchId) {
    return node;
  }
  if (node.nodes.length === 0) {
    return undefined;
  }

  for (let i = 0; i < node.nodes.length; i++) {
    if (node.nodes[i].id === searchId) {
      return node.nodes[i];
    }
    const exists = existsInTree(node.nodes[i], searchId);
    if (exists) {
      return exists;
    }
  }

  return undefined;
}

// If folders are completely empty, remove them from the customer selected folders
export function cleanUnusedDocumentServices(
  currentNode: ArchiveNodeOption,
  customerSelectedDocumentOptions: CustomerSelectedDocumentOption[]
) {
  return customerSelectedDocumentOptions.filter((node) => {
    const exists = existsInTree(currentNode, node.id);
    return exists && exists.nodes && exists.nodes.length === 0;
  });
}

// Find any "locked" nodes that are among the available options and add them to the customers selected folders
// if they aren't already present in the customers selected options
export function addLockedDocumentServicesToCustomerSelectedFolders(
  currentNode: ArchiveNodeOption,
  customerSelectedOptions: CustomerSelectedDocumentOption[]
) {
  currentNode.nodes.forEach((node) => {
    const isParentNode = node.nodes.length > 0;

    // Traverse down the tree
    if (isParentNode) {
      addLockedDocumentServicesToCustomerSelectedFolders(
        node,
        customerSelectedOptions
      );
      return;
    }

    const isLockedAndNotSelected =
      node.locked === true &&
      !customerSelectedOptions.find((service) => service.id === node.id);

    // If the node is "locked", then it needs to be selected
    if (isLockedAndNotSelected) {
      customerSelectedOptions.push({
        id: node.id,
        customerChoice: true,
        userIds: []
      });
    }
  });
}

/**
 * Enable document options that matches the given document option IDs
 *
 * @param existingDocumentOptions Existing customer selected documents options
 * @param documentOptionIds Document option IDs to add
 * @param defaultUsers Any users that should have permissions to add/remove files
 * @returns
 */
export function enableMultipleCustomerDocumentOptions(
  existingDocumentOptions: CustomerSelectedDocumentOption[],
  documentOptionIds: string[],
  defaultUsers: string[] = []
) {
  const added = documentOptionIds
    .filter(
      (id) => !existingDocumentOptions.map((node) => node.id).includes(id)
    )
    .map((id) => ({
      id: id,
      customerChoice: true,
      userIds: [...defaultUsers]
    }));

  return [...existingDocumentOptions, ...added];
}

/**
 * Disable document options that matches the given document option IDs
 *
 * @param existingDocumentOptions Existing customer selected documents options
 * @param documentOptionIds Document option IDs to remove
 * @returns
 */
export function disableMultipleCustomerDocumentOptions(
  existingDocumentOptions: CustomerSelectedDocumentOption[],
  documentOptionIds: string[]
) {
  return existingDocumentOptions.filter((service) => {
    return !documentOptionIds.includes(service.id);
  });
}

export function toggleCustomerSelectedDocumentOptions(
  customerSelectedDocumentOptions: CustomerSelectedDocumentOption[],
  documentOptionId: string,
  defaultUsers: string[] = []
) {
  const removed = remove(customerSelectedDocumentOptions, (service) => {
    return service.id === documentOptionId;
  });

  if (removed.length === 0) {
    customerSelectedDocumentOptions.push({
      id: documentOptionId,
      customerChoice: true,
      userIds: [...defaultUsers]
    });
  }

  return customerSelectedDocumentOptions;
}

const isAccountingOrDeclaration = (input: string): boolean => {
  // Reports folders are added here since users with
  // Accounting role will have access to these folders
  return (
    input.startsWith("eksk-red") ||
    input.startsWith("eksk-fin") ||
    isReportsFolder(input)
  );
};

const isPayroll = (input: string): boolean => {
  return input.startsWith("eksk-lon");
};

export function removeUserPermissionFromDocumentFolder(
  customerSelectedDocumentOptions: CustomerSelectedDocumentOption[],
  userId: string,
  role: ExternalUserRole
) {
  if (!customerSelectedDocumentOptions) {
    return [];
  }

  // If Admin role is removed
  // Remove from all folders
  if (role === ExternalUserRole.Admin) {
    return customerSelectedDocumentOptions.map((ds) => {
      ds.userIds = ds.userIds.filter((ui) => ui !== userId);
      return ds;
    });
  }

  // If Accounting role is removed
  // Remove only from Accounting or Declaration folders
  if (role === ExternalUserRole.Accounting) {
    return customerSelectedDocumentOptions.map((ds) => {
      if (isAccountingOrDeclaration(ds.id)) {
        ds.userIds = ds.userIds.filter((ui) => ui !== userId);
      }
      return ds;
    });
  }

  // If Payroll role is removed
  // Remove only from Payroll folders
  if (role === ExternalUserRole.Payroll) {
    return customerSelectedDocumentOptions.map((ds) => {
      if (isPayroll(ds.id)) {
        ds.userIds = ds.userIds.filter((ui) => ui !== userId);
      }
      return ds;
    });
  }

  // Default: return unchanged
  return customerSelectedDocumentOptions;
}

export function addUserPermissionOnDocumentFolder(
  customerSelectedDocumentOptions: CustomerSelectedDocumentOption[],
  userId: string,
  role: ExternalUserRole
) {
  if (!customerSelectedDocumentOptions) {
    return [];
  }

  // If Admin role is added
  // Add to all folders
  if (role === ExternalUserRole.Admin) {
    return customerSelectedDocumentOptions.map((ds) => {
      if (!ds.userIds.includes(userId)) {
        ds.userIds.push(userId);
      }
      return ds;
    });
  }

  // If Accounting role is added
  // Add to Accounting or Declaration folders
  if (role === ExternalUserRole.Accounting) {
    return customerSelectedDocumentOptions.map((ds) => {
      if (isAccountingOrDeclaration(ds.id)) {
        if (!ds.userIds.includes(userId)) {
          ds.userIds.push(userId);
        }
      }
      return ds;
    });
  }

  // If Payroll role is added
  // Add to Payroll folders
  if (role === ExternalUserRole.Payroll) {
    return customerSelectedDocumentOptions.map((ds) => {
      if (isPayroll(ds.id)) {
        if (!ds.userIds.includes(userId)) {
          ds.userIds.push(userId);
        }
      }
      return ds;
    });
  }

  // Default: return unchanged
  return customerSelectedDocumentOptions;
}

export function addMultipleUsersPermissionOnDocumentFolder(
  customerSelectedDocumentOptions: CustomerSelectedDocumentOption[],
  users: ExternalUser[]
) {
  if (!customerSelectedDocumentOptions || !users || users.length === 0) {
    return customerSelectedDocumentOptions ?? [];
  }

  // Clone array to avoid mutating original
  const updated = customerSelectedDocumentOptions.map((ds) => ({
    ...ds,
    userIds: [...ds.userIds]
  }));

  const addUserIf = (
    ds: CustomerSelectedDocumentOption,
    userId: string,
    condition: boolean
  ) => {
    if (condition && !ds.userIds.includes(userId)) {
      ds.userIds.push(userId);
    }
  };

  users.forEach(({ id, roles }) => {
    updated.forEach((ds) => {
      // Compute access once based on multiple roles
      const canAccess =
        roles.includes(ExternalUserRole.Admin) ||
        (roles.includes(ExternalUserRole.Accounting) &&
          isAccountingOrDeclaration(ds.id)) ||
        (roles.includes(ExternalUserRole.Payroll) && isPayroll(ds.id));

      addUserIf(ds, id, canAccess);
    });
  });

  return updated;
}

export function isGrandparentNode(service: ArchiveNodeOption) {
  return (
    service.nodes.length !== 0 &&
    service.nodes.filter((n) => n.nodes.length !== 0).length > 0
  );
}

export const getLeafNodeIds = (nodes: ArchiveNodeOption[]): string[] => {
  const ids: string[] = [];

  const traverse = (node: ArchiveNodeOption) => {
    if (!node.nodes || node.nodes.length === 0) {
      ids.push(node.id);
      return;
    }
    node.nodes.forEach(traverse);
  };

  nodes.forEach(traverse);
  return ids;
};

export const isReportsFolder = (folderId:string) => folderId.startsWith("reports")